import { createContext, useContext } from "react";

import type { WsStatus } from "@/api/ws";
import type { HealthResponse } from "@/api/client";
import type { SystemStateResult } from "@/hooks/useSystemState";
import type {
  FeedEvent,
  MarketStatusSnapshot,
  PriceTick,
  StreamHealthSnapshot,
} from "@/hooks/useWsConnection";

/**
 * Session-shared trading data. Lifted to a single owner ({@link AppShell}) so the
 * header renders identically on EVERY page (live price + stream health + emergency
 * state) without each page opening its own {@code /ws/trading} socket. The Dashboard
 * body reads the same context, so there is exactly one WS subscription per shell.
 */
export interface TradingData {
  wsStatus: WsStatus;
  wsEvents: FeedEvent[];
  latestPrice: PriceTick | null;
  marketStatus: MarketStatusSnapshot | null;
  streamHealth: StreamHealthSnapshot | null;
  systemState: SystemStateResult;
  health: HealthResponse | null;
}

/** Safe default so a component rendered WITHOUT the provider (isolated tests) degrades
 *  to "no data" rather than throwing — the same honesty contract the hooks use. */
const DEFAULT: TradingData = {
  wsStatus: "idle",
  wsEvents: [],
  latestPrice: null,
  marketStatus: null,
  streamHealth: null,
  systemState: { state: null, error: false, connecting: false, emergencyStop: null, refresh: () => {} },
  health: null,
};

export const TradingDataContext = createContext<TradingData>(DEFAULT);

/** Read the shared trading data (price, ws status, market status, system state, health). */
export function useTradingData(): TradingData {
  return useContext(TradingDataContext);
}
